// src/app/pages/CheckoutPage/index.tsx
import { useEffect, useMemo, useState } from "react";
import { ArrowLeft } from "lucide-react";
import { useLocation, useNavigate } from "react-router";
import { isCheckoutLocationState, type CheckoutLocationState } from "@/app/core/TokensPayment/checkOutComp/subscribe/shared/checkoutTypes";
import { emitNetworkStatus, runWithNetworkLifecycle, type NetworkLifecycleCallbacks, type NetworkStatusPayload } from "@/app/shared/lifecycle";
import { CheckoutPageController } from "./controller/CheckoutPageController";
import { OrderSummarySkeleton, TotalSectionSkeleton, PayActionSkeleton } from "./CheckoutSkeleton";
import {
  OrderSummarySection,
  PromoSection,
  TotalSection,
  PaymentMethodSection,
  PayActionSection,
} from "@/app/core/TokensPayment/checkOutComp/components/sections/index";
import { PaymentsApi, type CheckoutPreviewResponse } from '@/app/data/profilPayment/api/PaymentsApi';
import { useTranslation } from "@/app/core/ui/theme/LanguageProvider";

interface CheckoutPageProps {
  lifecycle?: NetworkLifecycleCallbacks;
}

export function CheckoutPage({ lifecycle }: CheckoutPageProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const state: CheckoutLocationState | null = isCheckoutLocationState(location.state) ? location.state : null;
  const checkoutController = useMemo(() => new CheckoutPageController(), []);
  const paymentsApi = useMemo(() => new PaymentsApi(), []);

  const product = checkoutController.resolveProduct(state);
  const [checkoutPreview, setCheckoutPreview] = useState<CheckoutPreviewResponse | null>(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(true);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [appliedPromo, setAppliedPromo] = useState<string | null>(state?.promoCode ?? null);
  const [isTelegramStarsSelected, setIsTelegramStarsSelected] = useState(false);

  const checkoutCurrency = (state as any)?.currency ?? 'XTR';

  const handlePromoStateChange = (payload: Omit<NetworkStatusPayload, "updatedAt">) => {
    emitNetworkStatus(lifecycle, payload);
  };

  useEffect(() => {
    let isCancelled = false;

    const loadPreview = async () => {
      const result = await runWithNetworkLifecycle({
        source: "CheckoutPreview",
        lifecycle,
        meta: { productCode: product.productCode, promoCode: appliedPromo ?? undefined },
        onStart: () => {
          if (isCancelled) return;
          setIsPreviewLoading(true);
          setPreviewError(null);
        },
        onError: (message) => {
          if (isCancelled) return;
          setPreviewError(message);
        },
        onFinally: () => {
          if (isCancelled) return;
          setIsPreviewLoading(false);
        },
        task: () => paymentsApi.previewCheckout({
          packageCode: product.productCode,
          currency: checkoutCurrency,   // ← заменено
          promoCode: appliedPromo ?? undefined,
        }),
      });

      if (!result || isCancelled) return;
      setCheckoutPreview(result);
      setAppliedPromo(result.applied?.promoCode ?? null);
    };

    void loadPreview();
    return () => {
      isCancelled = true;
    };
  }, [appliedPromo, lifecycle, paymentsApi, product.productCode]);

  const handlePreviewApplied = (preview: CheckoutPreviewResponse) => {
    setCheckoutPreview(preview);
    setAppliedPromo(preview.applied?.promoCode ?? null);
    setPreviewError(null);
  };

  const formattedSubtotal = checkoutPreview
    ? checkoutController.formatMinorAmount(checkoutPreview.baseAmountMinor, checkoutPreview.currency)
    : checkoutController.formatPrice(product.priceRub);
  const formattedTotal = checkoutPreview
    ? checkoutController.formatMinorAmount(checkoutPreview.finalAmountMinor, checkoutPreview.currency)
    : checkoutController.formatPrice(product.priceRub);

  // referralDiscountMinor и promoDiscountMinor могут быть не заданы в новом ответе
  const referralDiscountMinor = (checkoutPreview as any)?.referralDiscountMinor ?? 0;
  const promoDiscountMinor = (checkoutPreview as any)?.promoDiscountMinor ?? 0;
  const referralDiscountPercent = (checkoutPreview as any)?.referralDiscountPercent ?? 25;

  const formattedReferralDiscount =
    checkoutPreview && referralDiscountMinor > 0
      ? checkoutController.formatMinorAmount(referralDiscountMinor, checkoutPreview.currency)
      : null;
  const formattedPromoDiscount =
    checkoutPreview && promoDiscountMinor > 0
      ? checkoutController.formatMinorAmount(promoDiscountMinor, checkoutPreview.currency)
      : null;

  return (
    <div className="h-full min-h-0 overflow-y-auto overflow-x-hidden bg-transparent">
      <div
        className="sticky top-0 z-40 px-4 py-2 flex text-algin-center"
        style={{ paddingTop: "calc(env(safe-area-inset-top) + 15px)" }}
      >
        <div className="flex-shrink-0">
          <button onClick={() => navigate(-1)} className="-ml-2 rounded-lg p-2 transition-all hover:bg-white/20 dark:hover:bg-white/10 active:scale-95">
            <ArrowLeft className="h-5 w-5 text-algin-center" />
          </button>
        </div>
        <div className="flex-1 flex justify-center">
          <h1 className="text-lg font-bold text-foreground">{t('checkout')}</h1>
        </div>
        <div className="flex-shrink-0 w-10" />
      </div>

      <div className="mx-auto max-w-2xl px-4 sm:px-6 lg:px-8">
        {isPreviewLoading && !checkoutPreview
          ? <OrderSummarySkeleton />
          : <OrderSummarySection product={product} formattedSubtotal={formattedSubtotal} />
        }
        {previewError && !checkoutPreview && (
          <div className="mb-6 rounded-2xl border border-destructive/20 bg-destructive/10 p-4 text-sm text-destructive">
            {previewError}
          </div>
        )}
        <PromoSection
          product={product}
          appliedPromo={appliedPromo}
          onPreviewApplied={handlePreviewApplied}
          onStateChange={handlePromoStateChange}
        />
        {isPreviewLoading && !checkoutPreview
          ? <TotalSectionSkeleton />
          : (
            <TotalSection
              formattedBaseTotal={formattedSubtotal}
              formattedReferralDiscount={formattedReferralDiscount}
              referralDiscountPercent={referralDiscountPercent}
              formattedPromoDiscount={formattedPromoDiscount}
              formattedTotal={formattedTotal}
            />
          )
        }
        <PaymentMethodSection onMethodSelected={setIsTelegramStarsSelected} />
        {isPreviewLoading && !checkoutPreview
          ? <PayActionSkeleton />
          : (
            <PayActionSection
              product={product}
              appliedPromo={appliedPromo}
              isTelegramStarsSelected={isTelegramStarsSelected}
              formattedTotal={formattedTotal}
              isPriceReady={!isPreviewLoading && checkoutPreview !== null}
            />
          )
        }
      </div>
    </div>
  );
}